// CartHub Dashboard — Settings Component.
// Tab: Recovery, Email branding, DNS/Pro Tier, Personalizza Price Alert, Popup carrello, Account & API.
// `OnSitePopupSettings` è reso nella tab «Popup carrello» (dopo Price Alert).
// Include sotto-componenti: EmailTier, EmailBranding, EmailPreview, ColorField, RecoverySettings, RecoveryMarginProtectionCard, PriceAlertSetup.

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api, Ic, CopyBtn, LegendInfoIcon, useMediaMinWidth } from "../shared";
import { RecoveryMarginProtectionCard } from "./RecoveryMarginProtectionCard";
import PriceAlertSetup from "./PriceAlertSetup";
import PriceAlertWpScopeReadonly from "./PriceAlertWpScopeReadonly";
import OnSitePopupSettings from "./OnSitePopupSettings";

function EmailTier({ shopId, shopDomain }) {
  const { t } = useTranslation("settings");
  const [tier,         setTier]         = useState(null);
  const [status,       setStatus]       = useState('inactive');
  const [domain,       setDomain]       = useState('');
  const [senderPrefix, setSenderPrefix] = useState('');
  const [dnsRecs,      setDnsRecs]      = useState(null);
  const [dnsCheck,     setDnsCheck]     = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [initLoad,     setInitLoad]     = useState(true);
  const [err,          setErr]          = useState('');
  const [msg,          setMsg]          = useState('');
  const [spfInput,     setSpfInput]     = useState('');
  const [spfMerged,    setSpfMerged]    = useState('');
  const [spfOpen,      setSpfOpen]      = useState(false);
  const [guideOpen,    setGuideOpen]    = useState(false);

  useEffect(()=>{
    if (!shopId) return;
    Promise.all([
      api(`/settings/${shopId}/pro-tier`),
      api(`/settings/${shopId}`)
    ]).then(([d, s])=>{
      setTier(d.email_tier || 'quick_start');
      setStatus(d.pro_tier_status || 'inactive');
      setDomain(d.pro_domain || shopDomain || '');
      if (d.pro_dns_records && (d.email_tier === 'pro_pending' || d.email_tier === 'pro_active')) {
        setDnsRecs(d.pro_dns_records);
      }
      const existingPrefix = s.email_sender_prefix || '';
      const nameAsPrefix   = (s.email_sender_name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      setSenderPrefix(existingPrefix || nameAsPrefix);
    }).catch(()=>{}).finally(()=>setInitLoad(false));
  },[shopId]);

  if (initLoad) return <div style={{color:'#9ca3af',fontSize:15,padding:'8px 0'}}>{t("loading")}</div>;

  const activate = async () => {
    setLoading(true); setErr(''); setMsg('');
    try {
      const res = await api(`/settings/${shopId}/pro-tier/activate`, {
        method: 'POST',
        body: JSON.stringify({
          pro_domain:    domain,
          sender_prefix: senderPrefix.trim().toLowerCase() || null
        })
      });
      if (res.error) throw new Error(res.error);
      setDnsRecs(res.dns_records);
      setTier('pro_pending');
      setStatus('pending_dns');
      setMsg(t("emailTier.msgDomainCreated"));
    } catch(e){ setErr(e.message); }
    finally { setLoading(false); }
  };

  const verify = async () => {
    setLoading(true); setErr(''); setMsg('');
    try {
      const res = await api(`/settings/${shopId}/pro-tier/verify`, { method: 'POST' });
      if (res.error) throw new Error(res.error);
      setDnsCheck(res.dns_status);
      if (res.verified) { setTier('pro_active'); setStatus('active'); setMsg(t("emailTier.msgProActive")); }
      else { setMsg(t("emailTier.msgDnsPending")); }
    } catch(e){ setErr(e.message); }
    finally { setLoading(false); }
  };

  const deactivate = async () => {
    if (!window.confirm(t("confirm.proTierDeactivate"))) return;
    await api(`/settings/${shopId}/pro-tier/deactivate`, { method: 'POST' });
    setTier('quick_start'); setStatus('inactive'); setDnsRecs(null); setDnsCheck(null); setMsg('');
  };

  const mergeSPF = () => {
    const ourInclude = 'include:_spf.mailersend.net';
    const input = spfInput.trim();
    if (!input) { setSpfMerged(''); return; }
    if (input.includes(ourInclude)) {
      setSpfMerged(input);
      return;
    }
    const merged = input.replace(/([-~?+]all)/i, `${ourInclude} $1`);
    setSpfMerged(merged === input ? `${input} ${ourInclude}` : merged);
  };

  const DnsRow = ({ label, descKey, type, hostname, value }) => (
      <div className="dns-record">
        <div style={{marginBottom:6}}>
          <div className="dns-label">{label}</div>
          <div style={{fontSize:13,color:'#6b7280',lineHeight:1.4}}>{t(`dns.recordDesc.${descKey}`)}</div>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:8,marginTop:4}}>
          <div style={{display:'flex',alignItems:'center',gap:4,flexWrap:'wrap'}}>
            <span style={{fontSize:13,color:'#9ca3af',minWidth:50}}>{t("emailTier.dnsFieldType")}</span>
            <span className="dns-val">{type}</span>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:4,flexWrap:'wrap'}}>
            <span style={{fontSize:13,color:'#9ca3af',minWidth:50}}>{t("emailTier.dnsFieldName")}</span>
            <span className="dns-val" style={{flex:1}}>{hostname}</span>
            <CopyBtn text={hostname}/>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:4,flexWrap:'wrap'}}>
            <span style={{fontSize:13,color:'#9ca3af',minWidth:50}}>{t("emailTier.dnsFieldValue")}</span>
            <span className="dns-val" style={{flex:1}}>{value}</span>
            <CopyBtn text={value}/>
          </div>
        </div>
      </div>
    );

  return (
    <div>
      <h2 style={{marginBottom:4}}>{t("emailTier.title")}</h2>
      <p>{t("emailTier.subtitle")}</p>

      <div className="tier-selector">
        {/* Quick Start */}
        <div className={`tier-option${tier==='quick_start'?' active':''}`} onClick={tier!=='quick_start'?deactivate:undefined}>
          <div className="tier-radio"/>
          <h3>{t("emailTier.quickStartTitle")}</h3>
          <p>
            {t("emailTier.senderPrefixLabel")}{" "}
            <code style={{fontSize:15,color:'#0A85CF'}}>{t("emailTier.quickStartCodeLabel")}</code>
            <br/>
            {t("emailTier.quickStartRest")}
          </p>
        </div>

        {/* Pro Tier */}
        <div className={`tier-option${tier!=='quick_start'?' active':''}`} onClick={tier==='quick_start'?()=>setTier('pro_pending'):undefined}>
          <div className="tier-badge" style={{fontSize:15,padding:'2px 6px'}}>{t("emailTier.proBadge")}</div>
          <div className="tier-radio"/>
          <h3>{t("emailTier.proTitle")}</h3>
          <p>
            {t("emailTier.senderPrefixLabel")}{" "}
            <code style={{fontSize:15,color:'#0A85CF'}}>
              {senderPrefix || t("emailTier.proSenderPreview")}@{domain || t("emailTier.proCodeFallback")}
            </code>
            <br/>
            {t("emailTier.proRest")}
          </p>
        </div>
      </div>

      {/* Form attivazione Pro */}
      {tier==='pro_pending' && status==='inactive' && (
        <div>
          <div className="ch-field">
            <label>{t("emailTier.shopDomainLabel")}</label>
            <div className="ch-hint" style={{marginBottom:8}}>{t("emailTier.shopDomainHint")}</div>
            <input className="ch-input" style={{maxWidth:320}} type="text" value={domain} onChange={e=>setDomain(e.target.value)} placeholder="mionegozio.it"/>
          </div>

          <div className="ch-field">
            <label>{t("emailTier.senderNameLabel")}</label>
            <div className="ch-hint" style={{marginBottom:8}}>{t("emailTier.senderNameHint")}</div>
            <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
              <input
                className="ch-input"
                type="text"
                value={senderPrefix}
                onChange={e=>setSenderPrefix(e.target.value.toLowerCase().replace(/[^a-z0-9]/g,''))}
                placeholder={t("emailTier.senderPlaceholder")}
                maxLength={20}
                style={{maxWidth:160}}
              />
              <span style={{fontSize:14,color:'#6b7280',fontFamily:'monospace'}}>@{domain || t("emailTier.proCodeFallback")}</span>
            </div>
            {senderPrefix && domain && (
              <div style={{marginTop:6,fontSize:13,color:'#6b7280'}}>
                {t("emailTier.willSendFrom")}{" "}
                <code style={{color:'#0A85CF'}}>{senderPrefix}@{domain}</code>
              </div>
            )}
          </div>

          <button className="ch-btn ch-btn-primary" onClick={activate} disabled={loading||!domain}>
            {loading ? t("emailTier.activateLoading") : t("emailTier.activateBtn")}
          </button>
        </div>
      )}

      {/* Record DNS da aggiungere */}
      {dnsRecs && (tier==='pro_pending' || tier==='pro_active') && (
        <div style={{marginTop:16}}>

          {/* Mini-Guida DNS */}
          <div className="dns-guide">
            <div className="dns-guide-header" onClick={()=>setGuideOpen(o=>!o)}>
              <span style={{ display: 'inline-flex', color: 'var(--text2)' }}>{guideOpen ? Ic.chevronDown : Ic.chevronRight}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><span style={{ display: 'inline-flex', color: 'var(--text2)' }}>{Ic.book}</span>{t("emailTier.guideHeader")}</span>
            </div>
            {guideOpen && (
              <div className="dns-guide-body">

                <div className="dns-case" style={{background:'#f0fdf4',border:'1px solid #bbf7d0'}}>
                  <div className="dns-case-title"><span className="dns-step-badge">1</span> {t("dns.guide1Title")}</div>
                  <p>{t("dns.guide1p1")}</p>
                  <p style={{color:'#6b7280',fontSize:13,display:'flex',alignItems:'flex-start',gap:8}}><span style={{display:'inline-flex',flexShrink:0,marginTop:2,color:'var(--text2)'}}>{Ic.lightbulb}</span><span>{t("dns.guide1p2")}</span></p>
                </div>

                <div className="dns-case" style={{background:'#fffbeb',border:'1px solid #fde68a'}}>
                  <div className="dns-case-title"><span className="dns-step-badge">2</span> {t("dns.guide2Title")}</div>
                  <p>{t("dns.guide2p1")}</p>
                  <p>{t("dns.guide2p2")}</p>
                  <p><span style={{color:'#6b7280'}}>{t("dns.guide2existing")} </span><code>v=spf1 include:_spf.google.com ~all</code></p>
                  <p><span style={{color:'#059669'}}>{t("dns.guide2with")} </span><code>v=spf1 include:_spf.google.com include:_spf.mailersend.net ~all</code></p>
                  <p>{t("dns.guide2p3")}</p>
                </div>

                <div className="dns-case" style={{background:'#eff6ff',border:'1px solid #bfdbfe'}}>
                  <div className="dns-case-title"><span className="dns-step-badge">3</span> {t("dns.guide3Title")}</div>
                  <p>{t("dns.guide3p1")}</p>
                  <p>{t("dns.guide3p2")}</p>
                </div>

                <div style={{marginTop:12,padding:'10px 14px',background:'#f0faf8',border:'1px solid #b2d8d1',borderRadius:6,fontSize:13,color:'#6b7280',display:'flex',alignItems:'flex-start',gap:8}}>
                  <span style={{display:'inline-flex',flexShrink:0,color:'var(--text2)',marginTop:1}}>{Ic.clock}</span>
                  <span>{t("dns.guideTiming")}</span>
                </div>

              </div>
            )}
          </div>

          <div style={{fontWeight:600,fontSize:15,marginBottom:8}}>{t("emailTier.dnsRecordsTitle")}</div>
          {dnsRecs.spf      && <DnsRow label="SPF" descKey="SPF" type={dnsRecs.spf.type}           hostname={dnsRecs.spf.hostname}           value={dnsRecs.spf.value}/>}
          {dnsRecs.spf && (
            <div style={{marginBottom:12}}>
              <button
                type="button"
                className="ch-btn ch-btn-sm"
                onClick={()=>{ setSpfOpen(o=>!o); setSpfMerged(''); setSpfInput(''); }}
                style={{fontSize:13,color:'#6b7280',display:'inline-flex',alignItems:'center',gap:6}}
              >
                {spfOpen ? <><span style={{display:'inline-flex'}}>{Ic.x}</span>{t("emailTier.spfMergeToggleClose")}</> : <><span style={{display:'inline-flex'}}>{Ic.wrench}</span>{t("emailTier.spfMergeToggleOpen")}</>}
              </button>
              {spfOpen && (
                <div style={{marginTop:10,background:'#f9fafb',border:'1px solid #e5e5e5',borderRadius:6,padding:14}}>
                  <div style={{fontSize:14,fontWeight:500,marginBottom:6}}>{t("emailTier.spfPasteLabel")}</div>
                  <div style={{fontSize:13,color:'#6b7280',marginBottom:8}}>{t("emailTier.spfPasteExample")}{" "}<code style={{fontFamily:'monospace',color:'#0A85CF'}}>v=spf1 include:_spf.google.com ~all</code></div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
                    <input
                      className="ch-input"
                      style={{maxWidth:420,fontFamily:'monospace',fontSize:13}}
                      type="text"
                      value={spfInput}
                      onChange={e=>{ setSpfInput(e.target.value); setSpfMerged(''); }}
                      placeholder="v=spf1 include:_spf.google.com ~all"
                    />
                    <button className="ch-btn ch-btn-primary" onClick={mergeSPF} disabled={!spfInput}>{t("emailTier.spfMergeBtn")}</button>
                  </div>
                  {spfMerged && (
                    <div style={{marginTop:12}}>
                      <div style={{fontSize:13,fontWeight:500,marginBottom:4}}>{t("emailTier.spfMergedLabel")}</div>
                      <div style={{background:'#fff',border:'1px solid #0A85CF',borderRadius:6,padding:'10px 14px',fontFamily:'monospace',fontSize:13,wordBreak:'break-all',color:'#1a1a1a'}}>
                        {spfMerged}
                      </div>
                      <button
                        className="ch-btn ch-btn-sm"
                        style={{marginTop:8}}
                        onClick={()=>navigator.clipboard.writeText(spfMerged)}
                      ><span style={{display:'inline-flex',alignItems:'center',gap:6}}>{Ic.copy}{t("emailTier.copy")}</span></button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {dnsRecs.dkim        && <DnsRow label="DKIM" descKey="DKIM" type={dnsRecs.dkim.type}           hostname={dnsRecs.dkim.hostname}           value={dnsRecs.dkim.value}/>}
          {dnsRecs.return_path && <DnsRow label={t("dns.rowLabelReturnPath")} descKey="ReturnPath" type={dnsRecs.return_path.type}   hostname={dnsRecs.return_path.hostname}   value={dnsRecs.return_path.value}/>}
          {dnsRecs.custom_tracking && <DnsRow label={t("dns.rowLabelCustomTracking")} descKey="CustomTracking" type={dnsRecs.custom_tracking.type} hostname={dnsRecs.custom_tracking.hostname} value={dnsRecs.custom_tracking.value}/>}
          <div className="ch-hint" style={{marginBottom:12}}>{t("emailTier.dnsAfterHint")}</div>
          <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
            <button className="ch-btn ch-btn-primary" onClick={verify} disabled={loading}>{loading ? t("emailTier.verifyLoading") : t("emailTier.verifyBtn")}</button>
            {dnsCheck && (
              <div className="dns-status-row">
                <span className={`ch-status ${dnsCheck.dkim?'success':'warning'}`} style={{display:'inline-flex',alignItems:'center',gap:4}}>DKIM {dnsCheck.dkim?<span style={{display:'inline-flex'}}>{Ic.check}</span>:<span style={{display:'inline-flex'}}>{Ic.loader}</span>}</span>
                <span className={`ch-status ${dnsCheck.spf?'success':'warning'}`} style={{display:'inline-flex',alignItems:'center',gap:4}}>SPF {dnsCheck.spf?<span style={{display:'inline-flex'}}>{Ic.check}</span>:<span style={{display:'inline-flex'}}>{Ic.loader}</span>}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pro attivo */}
      {tier==='pro_active' && (
        <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap',marginTop:20,paddingTop:20,borderTop:'1px solid #e5e5e5'}}>
          <span className="ch-status success" style={{display:'inline-flex',alignItems:'center',gap:4}}><span style={{display:'inline-flex'}}>{Ic.check}</span>{t("emailTier.proActiveBadge")}</span>
          <span style={{fontSize:14,color:'#6b7280'}}>
            {t("emailTier.senderPrefixLabel")}{" "}
            <code style={{color:'#0A85CF',fontSize:15}}>{senderPrefix || "noreply"}@{domain}</code>
          </span>
          <button className="ch-btn ch-btn-sm" onClick={deactivate} style={{marginLeft:'auto'}}>{t("emailTier.backQuickStart")}</button>
        </div>
      )}

      {err && <div className="ch-info" style={{background:'#fff5f5',border:'1px solid #ffcdd2',color:'#c62828',marginTop:12}}>{err}</div>}
      {msg && <div className="ch-info" style={{marginTop:12}}>{msg}</div>}
    </div>
  );
}

// ══════════════════ EMAIL BRANDING ══════════════════
function contrastColor(hex) {
  try {
    const r=parseInt(hex.slice(1,3),16), g=parseInt(hex.slice(3,5),16), b=parseInt(hex.slice(5,7),16);
    return (0.299*r+0.587*g+0.114*b)/255 > 0.5 ? '#1a1a1a' : '#ffffff';
  } catch { return '#ffffff'; }
}

function ColorField({ label, hint, value, onChange }) {
  const { t } = useTranslation("settings");
  const [hex, setHex] = useState(value);
  useEffect(()=>setHex(value),[value]);
  const handleHex = e => {
    setHex(e.target.value);
    if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(e.target.value)) onChange(e.target.value);
  };
  return (
    <div className="color-field">
      <label>{label}</label>
      {hint && <div className="hint">{hint}</div>}
      <div className="color-row">
        <input type="color" value={value} onChange={e=>{onChange(e.target.value);setHex(e.target.value);}}/>
        <input className="color-hex" type="text" value={hex} onChange={handleHex} maxLength={7} placeholder="#4CAF50"/>
        <div className="color-swatch" style={{background:value}}/>
        <button className="color-reset" onClick={()=>{onChange('#4CAF50');setHex('#4CAF50');}}>{t("colorField.reset")}</button>
      </div>
    </div>
  );
}

function EmailPreview({ headerColor, accentColor, logoUrl, shopName, template = 'full' }) {
  const { t } = useTranslation("settings");
  const tc = contrastColor(headerColor);
  const ac = contrastColor(accentColor);
  const shop = shopName || t("branding.previewShopPlaceholder");
  const fullBullets = t("preview.fullBullets");

  if (template === 'light') {
    return (
      <div className="ep-wrap" style={{fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif'}}>
        <div style={{borderBottom:'1px solid #f0f0f0',paddingBottom:14,marginBottom:20}}>
          {logoUrl
            ? <img src={logoUrl} alt={shopName} style={{maxHeight:32,maxWidth:140,objectFit:'contain',display:'block'}} onError={e=>e.target.style.display='none'}/>
            : <span style={{fontSize:11,color:'#aaa',letterSpacing:'1.5px',textTransform:'uppercase'}}>{shop}</span>
          }
        </div>
        <p style={{margin:'0 0 8px',fontSize:17,color:'#1a1a1a',fontWeight:600}}>{t("preview.lightGreeting")}</p>
        <p style={{margin:'0 0 22px',fontSize:14,color:'#555',lineHeight:1.7}}>{t("preview.lightLead")}</p>
        <div style={{borderLeft:`3px solid ${accentColor}`,background:'#fafafa',borderRadius:'0 6px 6px 0',padding:'14px 16px',marginBottom:18}}>
          <p style={{margin:'0 0 4px',fontSize:15,color:'#1a1a1a',fontWeight:700}}>{t("preview.productTitle")}</p>
          <p style={{margin:'0',fontSize:13,color:'#555',lineHeight:1.7}}>{t("preview.productBullets")}</p>
        </div>
        <p style={{margin:'0 0 22px',fontSize:13,color:'#aaa',textTransform:'uppercase',letterSpacing:'1.5px'}}>{t("preview.cartSection")}</p>
        <div style={{borderBottom:'1px solid #f0f0f0',paddingBottom:10,marginBottom:10}}>
          <span style={{fontSize:14,color:'#1a1a1a',fontWeight:600}}>{t("preview.productTitle")}</span>
          <span style={{float:'right',fontSize:14,color:accentColor,fontWeight:700}}>€349,90</span>
        </div>
        <p style={{margin:'10px 0 22px',fontSize:15,color:'#1a1a1a',textAlign:'right',fontWeight:700}}>{t("preview.total")}</p>
        <span style={{display:'inline-block',background:accentColor,color:ac,padding:'12px 26px',borderRadius:50,fontWeight:700,fontSize:14}}>
          {t("preview.cta")}
        </span>
        <p style={{margin:'28px 0 0',fontSize:13,color:'#6b7280',lineHeight:1.8}}>
          {t("preview.signoff")}<br/><strong>{t("preview.signoffName")}</strong>
        </p>
        <p style={{margin:'18px 0 0',paddingTop:14,borderTop:'1px solid #f0f0f0',fontSize:11,color:'#bbb',lineHeight:2}}>
          {shop} · {t("preview.domainPlaceholder")}
          <br/>
          {t("preview.contactQuestion")}{' '}
          <span style={{color:'#bbb',textDecoration:'underline',cursor:'default'}}>{t("preview.replyEmailPlaceholder")}</span>
          <br/>
          <span style={{textDecoration:'underline',cursor:'default'}}>{t("preview.unsubscribeLong")}</span>
          {' · '}
          <span style={{textDecoration:'underline',cursor:'default'}}>{t("preview.privacyPolicy")}</span>
        </p>
      </div>
    );
  }

  return (
    <div className="ep-wrap">
      <div className="ep-header" style={{background:headerColor}}>
        {logoUrl
          ? <img src={logoUrl} alt={shopName} style={{maxHeight:44,maxWidth:160,objectFit:'contain',display:'block',margin:'0 auto'}} onError={e=>e.target.style.display='none'}/>
          : <span style={{color:tc,fontWeight:700,fontSize:16}}>{shop}</span>
        }
      </div>
      <div className="ep-body">
        <p style={{margin:'0 0 8px',fontWeight:600,fontSize:15,color:'#1a1a1a'}}>{t("preview.fullGreeting")}</p>
        <p style={{margin:'0 0 14px',color:'#6b7280',fontSize:14}}>{t("preview.fullLead")}</p>
        <div className="ep-product">
          <div style={{color:accentColor,fontWeight:700,fontSize:15,marginBottom:6}}>{t("preview.productTitle")}</div>
          <div style={{color:'#6b7280',fontSize:15,lineHeight:1.6,whiteSpace:'pre-line'}}>
            {fullBullets}
          </div>
        </div>
        <div className="ep-price-row">
          <span>{t("preview.cartTotalRow")}</span>
          <span style={{color:accentColor,fontWeight:700,fontSize:15}}>€349,90</span>
        </div>
        <div className="ep-cta">
          <span style={{background:accentColor,color:ac,padding:'9px 22px',borderRadius:50,fontWeight:600,fontSize:15,display:'inline-block'}}>
            {t("preview.fullCta")}
          </span>
        </div>
      </div>
      <div className="ep-footer">
        <div style={{ marginBottom: 8, lineHeight: 1.6 }}>{t("preview.footerSent", { shop })}</div>
        <div style={{ lineHeight: 1.6 }}>
          <span style={{ textDecoration: 'underline', cursor: 'default' }}>{t("preview.unsubscribe")}</span>
          <span> · </span>
          <span style={{ textDecoration: 'underline', cursor: 'default' }}>{t("preview.privacyPolicy")}</span>
          <span> · </span>
          <span style={{ textDecoration: 'underline', cursor: 'default' }}>{t("preview.replyEmailPlaceholder")}</span>
        </div>
      </div>
    </div>
  );
}

function EmailBranding({ shopId }) {
  const { t } = useTranslation("settings");
  const [headerColor,    setHeaderColor]    = useState('#0A85CF');
  const [accentColor,    setAccentColor]    = useState('#0A85CF');
  const [logoInput,      setLogoInput]      = useState('');
  const [senderPrefix,   setSenderPrefix]   = useState('');
  const [senderName,     setSenderName]     = useState('');
  const [emailTemplate,  setEmailTemplate]  = useState('full');
  const [emailTier,      setEmailTier]      = useState('quick_start');
  const [proDomain,      setProDomain]      = useState('');
  const [saving,         setSaving]         = useState(false);
  const [saved,          setSaved]          = useState(false);
  const [err,            setErr]            = useState('');
  const [initLoad,       setInitLoad]       = useState(true);

  useEffect(()=>{
    if (!shopId) return;
    Promise.all([
      api(`/settings/${shopId}`),
      api(`/settings/${shopId}/pro-tier`)
    ]).then(([d, pt])=>{
      setHeaderColor(d.email_header_color||'#0A85CF');
      setAccentColor(d.email_accent_color||'#0A85CF');
      setLogoInput(d.email_logo_url||'');
      setSenderPrefix(d.email_sender_prefix||'');
      setSenderName(d.email_sender_name||'');
      setEmailTemplate(d.email_template||'full');
      setEmailTier(pt.email_tier||'quick_start');
      setProDomain(pt.pro_domain||'');
    }).catch(()=>{}).finally(()=>setInitLoad(false));
  },[shopId]);

  if (initLoad) return <div style={{color:'#9ca3af',fontSize:15,padding:'8px 0'}}>{t("loading")}</div>;

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const res = await api(`/settings/${shopId}`,{
        method:'PUT',
        body: JSON.stringify({
          email_header_color:  headerColor,
          email_accent_color:  accentColor,
          email_logo_url:      logoInput||null,
          email_sender_prefix: senderPrefix.trim().toLowerCase()||null,
          email_sender_name:   senderName.trim()||null,
          email_template:      emailTemplate
        })
      });
      if (res.error) throw new Error(res.error);
      setSaved(true); setTimeout(()=>setSaved(false),2500);
    } catch(e){ setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="branding-wrap">
      <div className="branding-form">
        <h2 style={{marginBottom:4}}>{t("branding.title")}</h2>
        <p>{t("branding.subtitle")}</p>

        <div className="ch-field">
          <label>{t("branding.logoLabel")}</label>
          <div className="ch-hint" style={{marginBottom:8}}>{t("branding.logoHint")}</div>
          <input className="ch-input full" type="url" value={logoInput} onChange={e=>setLogoInput(e.target.value)} placeholder={t("branding.logoPlaceholder")}/>
          {logoInput && <button className="color-reset" style={{marginTop:6,display:'block'}} onClick={()=>setLogoInput('')}>{t("branding.removeLogo")}</button>}
        </div>

        <hr className="ch-divider"/>
        <div className="ch-field">
          <label>{t("branding.senderPersonLabel")}</label>
          <div className="ch-hint" style={{marginBottom:8}}>{t("branding.senderPersonHint")}</div>
          <input
            className="ch-input"
            type="text"
            value={senderName}
            onChange={e => setSenderName(e.target.value.replace(/[^a-zA-ZÀ-ÿ\s'-]/g,''))}
            placeholder={t("branding.senderPersonPlaceholder")}
            maxLength={30}
            style={{maxWidth:200}}
          />
          {senderName && (
            <div style={{marginTop:6,fontSize:13,color:'#6b7280'}}>
              {t("branding.senderPreview", { name: senderName, domain: proDomain || t("emailTier.proCodeFallback") })}
            </div>
          )}
          {emailTier === 'pro_active' && proDomain && (
            <div style={{marginTop:8,fontSize:13,color:'#9ca3af',background:'#f9fafb',border:'1px solid #e5e7eb',borderRadius:6,padding:'8px 12px'}}>
              {t("branding.proTierHint", {
                email: `${senderPrefix || (senderName ? senderName.toLowerCase().replace(/[^a-z0-9]/g,'') : t("emailTier.proSenderPreview"))}@${proDomain}`
              })}
            </div>
          )}
        </div>

        <hr className="ch-divider"/>

        <div className="ch-field">
          <label>{t("branding.styleLabel")}</label>
          <div className="ch-hint" style={{marginBottom:10}}>{t("branding.styleHint")}</div>
          <div style={{display:'flex',gap:10}}>
            {[['full', t("branding.styleFull")],['light', t("branding.styleLight")]].map(([val,label])=>(
              <button
                key={val}
                onClick={()=>setEmailTemplate(val)}
                style={{
                  padding:'8px 20px',borderRadius:8,fontSize:14,fontWeight:500,cursor:'pointer',
                  border: emailTemplate===val ? '2px solid #0A85CF' : '1.5px solid #e5e7eb',
                  background: emailTemplate===val ? '#fff7f4' : '#fff',
                  color: emailTemplate===val ? '#0A85CF' : '#6b7280',
                  transition:'all .15s'
                }}
              >{label}{val==='full'&&<span style={{marginLeft:6,fontSize:11,background:'#0A85CF',color:'#fff',borderRadius:4,padding:'1px 5px'}}>{t("branding.styleDefaultBadge")}</span>}</button>
            ))}
          </div>
        </div>

        <hr className="ch-divider"/>

        <ColorField
          label={t("branding.colorHeader")}
          hint={t("branding.colorHeaderHint")}
          value={headerColor}
          onChange={setHeaderColor}
        />
        <ColorField
          label={t("branding.colorAccent")}
          hint={t("branding.colorAccentHint")}
          value={accentColor}
          onChange={setAccentColor}
        />

        {err && <div className="ch-info" style={{background:'#fff5f5',border:'1px solid #ffcdd2',color:'#c62828',marginBottom:12}}>{err}</div>}

        <div className="ch-actions">
          <button className="ch-btn ch-btn-primary" onClick={save} disabled={saving}>
            {saving ? t("branding.saveLoading") : saved ? <>{Ic.check} {t("branding.saved")}</> : t("branding.saveBtn")}
          </button>
        </div>
        <div className="ch-hint" style={{marginTop:8}}>{t("branding.applyHint")}</div>
      </div>

      <div className="branding-preview">
        <h2 style={{marginBottom:4}}>{t("branding.previewTitle")}</h2>
        <p>{t("branding.previewSubtitle")}</p>
        <EmailPreview headerColor={headerColor} accentColor={accentColor} logoUrl={logoInput} shopName={t("branding.previewShopPlaceholder")} template={emailTemplate}/>
      </div>
    </div>
  );
}

// ══════════════════ RECOVERY SETTINGS ══════════════════
/** Icona sezione: tetti / soglie (linee di riferimento) */
function HighValueLimitsIcon({ size = 22 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ flexShrink: 0, color: "#0A85CF" }}
    >
      <path d="M5 10h14M5 14h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M12 7v1.5M12 15.5V17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity={0.4} />
    </svg>
  );
}

/** Legenda `.ch-help-legend` per soglia / tetti recovery (stesso pattern di Popup carrello). */
function RecoveryHighValueHelpLegend() {
  const { t } = useTranslation("settings");
  const isDesktop = useMediaMinWidth(900);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!isDesktop) setMobileOpen(false);
  }, [isDesktop]);

  const panelId = "recovery-high-value-help-panel";
  const showPanel = isDesktop || mobileOpen;

  return (
    <section className="ch-help-legend ch-help-legend--embed" aria-label={t("recovery.highValue.aria")}>
      <div className="ch-help-legend__accent" aria-hidden="true" />
      {!isDesktop && (
        <button
          type="button"
          className="ch-help-legend__summary"
          aria-expanded={mobileOpen}
          aria-controls={panelId}
          onClick={() => setMobileOpen((o) => !o)}
        >
          <span className="ch-help-legend__sum-icon">
            <LegendInfoIcon size={18} />
          </span>
          <span>
            {t("recovery.highValue.summaryTitle")}
            <span className="ch-help-legend__sum-hint">{t("recovery.highValue.summaryHint")}</span>
          </span>
          <span className="ch-help-legend__chev" aria-hidden="true" />
        </button>
      )}
      {showPanel && (
        <div id={panelId} className="ch-help-legend__panel">
          <header className="ch-help-legend__head ch-help-legend__head--mobile-only">
            <div className="ch-help-legend__head-icon" aria-hidden="true">
              <LegendInfoIcon />
            </div>
            <div>
              <h2 className="ch-help-legend__title">{t("recovery.highValue.title")}</h2>
              <p className="ch-help-legend__sub">{t("recovery.highValue.subMobile")}</p>
            </div>
          </header>

          <div className="ch-help-legend__desktop">
            <div className="ch-help-legend__desktop-intro">
              <div className="ch-help-legend__head-icon" aria-hidden="true">
                <LegendInfoIcon size={20} />
              </div>
              <div>
                <h2 className="ch-help-legend__title">{t("recovery.highValue.title")}</h2>
                <p className="ch-help-legend__sub">{t("recovery.highValue.subDesktop")}</p>
              </div>
            </div>
            <div className="ch-help-legend__grid ch-help-legend__grid--3">
              <div className="ch-help-legend__cell">
                <div className="ch-help-legend__cell-rail ch-help-legend__cell-rail--neutral" aria-hidden="true" />
                <span className="ch-status neutral">
                  <span className="status-dot" />
                  {t("recovery.highValue.labelThresholdShort")}
                </span>
                <p className="ch-help-legend__cell-text">{t("recovery.highValue.threshShort")}</p>
              </div>
              <div className="ch-help-legend__cell">
                <div className="ch-help-legend__cell-rail ch-help-legend__cell-rail--warning" aria-hidden="true" />
                <span className="ch-status warning">
                  <span className="status-dot" />
                  {t("recovery.highValue.labelPctShort")}
                </span>
                <p className="ch-help-legend__cell-text">{t("recovery.highValue.pctShort")}</p>
              </div>
              <div className="ch-help-legend__cell">
                <div className="ch-help-legend__cell-rail ch-help-legend__cell-rail--success" aria-hidden="true" />
                <span className="ch-status success">
                  <span className="status-dot" />
                  {t("recovery.highValue.labelEurShort")}
                </span>
                <p className="ch-help-legend__cell-text">{t("recovery.highValue.eurShort")}</p>
              </div>
            </div>
          </div>

          <div className="ch-help-legend__list">
            <div className="ch-help-legend__row">
              <div className="ch-help-legend__rail ch-help-legend__rail--neutral" aria-hidden="true" />
              <div className="ch-help-legend__body">
                <span className="ch-status neutral">
                  <span className="status-dot" />
                  {t("recovery.highValue.labelThresholdLong")}
                </span>
                <p className="ch-help-legend__text">{t("recovery.highValue.threshFull")}</p>
              </div>
            </div>
            <div className="ch-help-legend__row">
              <div className="ch-help-legend__rail ch-help-legend__rail--warning" aria-hidden="true" />
              <div className="ch-help-legend__body">
                <span className="ch-status warning">
                  <span className="status-dot" />
                  {t("recovery.highValue.labelPctLong")}
                </span>
                <p className="ch-help-legend__text">{t("recovery.highValue.pctFull")}</p>
              </div>
            </div>
            <div className="ch-help-legend__row">
              <div className="ch-help-legend__rail ch-help-legend__rail--success" aria-hidden="true" />
              <div className="ch-help-legend__body">
                <span className="ch-status success">
                  <span className="status-dot" />
                  {t("recovery.highValue.labelEurLong")}
                </span>
                <p className="ch-help-legend__text">{t("recovery.highValue.eurFull")}</p>
              </div>
            </div>
          </div>

          <p className="ch-help-legend__tip">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path
                d="M4 8V6a2 2 0 012-2h2M20 8V6a2 2 0 00-2-2h-2M4 16v2a2 2 0 002 2h2M20 16v2a2 2 0 01-2 2h-2"
                strokeLinecap="round"
              />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <span>{t("recovery.highValue.tip")}</span>
          </p>
        </div>
      )}
    </section>
  );
}

function buildFallbackCouponRules(t) {
  return [
    { id: "1", label: t("recovery.rulesFallbackBand1"), max_discount_percent: 15, enabled: true },
    { id: "2", label: t("recovery.rulesFallbackBand2"), max_discount_percent: 12, enabled: true },
    { id: "3", label: t("recovery.rulesFallbackBand3"), max_discount_percent: 8, enabled: true },
    { id: "4", label: t("recovery.rulesFallbackBand4"), max_discount_percent: 5, enabled: true },
  ];
}

/** DB seed (`auth.js` / `agency.js`) stores Italian labels; merchant API returns them as-is. For the four base bands (category null, standard EUR brackets), show dashboard locale instead of `label`. */
function localisedDefaultBandLabel(r, t) {
  if (r.category != null && r.category !== "") return null;
  const min = Number(r.min_cart_value);
  const max = r.max_cart_value == null || r.max_cart_value === "" ? null : Number(r.max_cart_value);
  if (!Number.isFinite(min)) return null;
  if (max !== null && !Number.isFinite(max)) return null;
  if (min === 0 && max === 50) return t("recovery.rulesFallbackBand1");
  if (min === 50 && max === 100) return t("recovery.rulesFallbackBand2");
  if (min === 100 && max === 200) return t("recovery.rulesFallbackBand3");
  if (min === 200 && max === null) return t("recovery.rulesFallbackBand4");
  return null;
}

function RecoverySettings({ shopId, emailPaused, silentUntil, onTogglePause }) {
  const { t, i18n } = useTranslation("settings");
  const { t: tEmailUi } = useTranslation("emails");
  const [rules, setRules] = useState([]);
  const [rulesSaved, setRulesSaved] = useState(false);
  const [rulesLoading, setRulesLoading] = useState(true);

  const [r1on, setR1on] = useState(true);
  const [r1delay, setR1delay] = useState(60);
  const [r2delay, setR2delay] = useState(6);
  const [r3delay, setR3delay] = useState(18);
  const [r2on, setR2on] = useState(true);
  const [r3on, setR3on] = useState(true);
  const [timingMode, setTimingMode] = useState('auto');
  const [seqSaved, setSeqSaved] = useState(false);
  const [seqLoading, setSeqLoading] = useState(true);

  const [capThreshStr, setCapThreshStr] = useState('');
  const [capMaxPctStr, setCapMaxPctStr] = useState('');
  const [capMaxEurStr, setCapMaxEurStr] = useState('');
  const [capsSaved, setCapsSaved] = useState(false);
  const [capsSaving, setCapsSaving] = useState(false);
  const [capsErr, setCapsErr] = useState('');

  const [blockLinePromo, setBlockLinePromo] = useState(false);
  const [linePromoThreshStr, setLinePromoThreshStr] = useState('15');
  const [blockCartCouponSnap, setBlockCartCouponSnap] = useState(false);
  const [couponNoStack, setCouponNoStack] = useState(false);
  const [marginSaved, setMarginSaved] = useState(false);
  const [marginSaving, setMarginSaving] = useState(false);
  const [marginErr, setMarginErr] = useState('');

  const [bccEnabled, setBccEnabled] = useState(false);
  const [bccEmail, setBccEmail] = useState('');
  const [bccR, setBccR] = useState(true);
  const [bccPa, setBccPa] = useState(false);
  const [bccPoe, setBccPoe] = useState(false);
  const [bccSaving, setBccSaving] = useState(false);
  const [bccErr, setBccErr] = useState('');
  const [bccSaved, setBccSaved] = useState(false);

  useEffect(() => {
    if (!shopId) return;
    setRulesLoading(true);
    api(`/coupon-rules/${shopId}`).then(data => {
      if (Array.isArray(data)) setRules(data);
      else setRules(buildFallbackCouponRules(t));
    }).catch(() => setRules(buildFallbackCouponRules(t))).finally(() => setRulesLoading(false));
  }, [shopId, i18n.language, t]);

  useEffect(() => {
    if (!shopId) return;
    api(`/settings/${shopId}`).then(d => {
      if (d.email1_enabled !== undefined) setR1on(d.email1_enabled);
      if (d.email1_delay_minutes !== undefined) setR1delay(d.email1_delay_minutes);
      if (d.email2_delay_hours !== undefined) setR2delay(d.email2_delay_hours);
      if (d.email3_delay_hours !== undefined) setR3delay(d.email3_delay_hours);
      if (d.email2_enabled !== undefined) setR2on(d.email2_enabled);
      if (d.email3_enabled !== undefined) setR3on(d.email3_enabled);
      if (d.timing_mode) setTimingMode(d.timing_mode);
      setCapThreshStr(
        d.recovery_high_cart_threshold_eur != null && d.recovery_high_cart_threshold_eur !== ''
          ? String(d.recovery_high_cart_threshold_eur)
          : ''
      );
      setCapMaxPctStr(
        d.recovery_high_cart_max_percent != null && d.recovery_high_cart_max_percent !== ''
          ? String(d.recovery_high_cart_max_percent)
          : ''
      );
      setCapMaxEurStr(
        d.recovery_max_discount_eur != null && d.recovery_max_discount_eur !== ''
          ? String(d.recovery_max_discount_eur)
          : ''
      );
      if (d.recovery_discount_block_line_promo !== undefined) setBlockLinePromo(!!d.recovery_discount_block_line_promo);
      if (d.recovery_discount_line_promo_threshold_pct != null && d.recovery_discount_line_promo_threshold_pct !== '') {
        setLinePromoThreshStr(String(d.recovery_discount_line_promo_threshold_pct));
      }
      if (d.recovery_discount_block_cart_coupon !== undefined) setBlockCartCouponSnap(!!d.recovery_discount_block_cart_coupon);
      if (d.recovery_coupon_no_stack_with_others !== undefined) setCouponNoStack(!!d.recovery_coupon_no_stack_with_others);
      if (d.merchant_copy_bcc_enabled !== undefined) setBccEnabled(!!d.merchant_copy_bcc_enabled);
      setBccEmail(d.merchant_copy_bcc_email != null ? String(d.merchant_copy_bcc_email) : '');
      if (d.merchant_copy_bcc_recovery !== undefined) setBccR(!!d.merchant_copy_bcc_recovery);
      if (d.merchant_copy_bcc_pa !== undefined) setBccPa(!!d.merchant_copy_bcc_pa);
      if (d.merchant_copy_bcc_poe !== undefined) setBccPoe(!!d.merchant_copy_bcc_poe);
    }).catch(()=>{}).finally(() => setSeqLoading(false));
  }, [shopId]);

  const saveRules = async () => {
    try {
      await api(`/coupon-rules/${shopId}`, { method:'PUT', body: JSON.stringify({ rules }) });
      setRulesSaved(true); setTimeout(()=>setRulesSaved(false), 2000);
    } catch(e) { console.error('Save rules error:', e); }
  };

  const saveSequence = async () => {
    try {
      await api(`/settings/${shopId}`, { method:'PUT', body: JSON.stringify({
        email1_enabled: r1on,
        email1_delay_minutes: r1delay,
        email2_delay_hours: r2delay,
        email3_delay_hours: r3delay,
        email2_enabled: r2on,
        email3_enabled: r3on && r2on,
        timing_mode: timingMode,
      })});
      setSeqSaved(true); setTimeout(()=>setSeqSaved(false), 2000);
    } catch(e) { console.error('Save sequence error:', e); }
  };

  const saveMarginProtection = async () => {
    setMarginSaving(true);
    setMarginErr('');
    const rawTh = String(linePromoThreshStr || '').trim().replace(',', '.');
    const th = rawTh === '' ? 15 : parseFloat(rawTh);
    if (!Number.isFinite(th) || th < 0 || th > 100) {
      setMarginErr(t("recovery.validation.linePromoThreshold"));
      setMarginSaving(false);
      return;
    }
    try {
      const res = await api(`/settings/${shopId}`, {
        method: 'PUT',
        body: JSON.stringify({
          recovery_discount_block_line_promo: blockLinePromo,
          recovery_discount_line_promo_threshold_pct: th,
          recovery_discount_block_cart_coupon: blockCartCouponSnap,
          recovery_coupon_no_stack_with_others: couponNoStack,
        }),
      });
      if (res.error) throw new Error(res.error);
      setMarginSaved(true);
      setTimeout(() => setMarginSaved(false), 2000);
    } catch (e) {
      setMarginErr(e.message || t("recovery.validation.saveFailed"));
    } finally {
      setMarginSaving(false);
    }
  };

  const saveRecoveryCaps = async () => {
    setCapsSaving(true);
    setCapsErr('');
    const parseOpt = (s, { max, min, nameKey }) => {
      const raw = String(s ?? '').trim();
      if (raw === '') return null;
      const n = parseFloat(raw.replace(',', '.'));
      const fieldLabel = t(`recovery.validation.${nameKey}`);
      if (!Number.isFinite(n)) throw new Error(t("recovery.validation.invalidNumber", { field: fieldLabel }));
      if (n < min || n > max) throw new Error(t("recovery.validation.outOfRange", { field: fieldLabel, min, max }));
      return n;
    };
    try {
      const body = {
        recovery_high_cart_threshold_eur: parseOpt(capThreshStr, { min: 100, max: 500000, nameKey: "fieldThreshold" }),
        recovery_high_cart_max_percent: parseOpt(capMaxPctStr, { min: 0, max: 20, nameKey: "fieldMaxPct" }),
        recovery_max_discount_eur: parseOpt(capMaxEurStr, { min: 0, max: 50000, nameKey: "fieldMaxEur" }),
      };
      const res = await api(`/settings/${shopId}`, { method: 'PUT', body: JSON.stringify(body) });
      if (res.error) throw new Error(res.error);
      setCapsSaved(true);
      setTimeout(() => setCapsSaved(false), 2000);
    } catch (e) {
      setCapsErr(e.message || t("recovery.validation.saveFailed"));
    } finally {
      setCapsSaving(false);
    }
  };

  const saveMerchantBcc = async () => {
    setBccSaving(true);
    setBccErr('');
    const bccApiErrorMsg = (res) => {
      const code = typeof res?.error_code === 'string' ? res.error_code : '';
      if (code && i18n.exists(`recovery.bcc.errors.${code}`, { ns: 'settings' })) {
        return t(`recovery.bcc.errors.${code}`);
      }
      if (typeof res?.error === 'string' && res.error.trim()) return res.error;
      return t('recovery.validation.saveFailed');
    };
    try {
      const res = await api(`/settings/${shopId}`, {
        method: 'PUT',
        body: JSON.stringify({
          merchant_copy_bcc_enabled: bccEnabled,
          merchant_copy_bcc_email: bccEmail.trim(),
          merchant_copy_bcc_recovery: bccR,
          merchant_copy_bcc_pa: bccPa,
          merchant_copy_bcc_poe: bccPoe,
        }),
      });
      if (res.error || res.error_code) {
        setBccErr(bccApiErrorMsg(res));
        return;
      }
      setBccSaved(true);
      setTimeout(() => setBccSaved(false), 2000);
    } catch (e) {
      setBccErr(e.message || t("recovery.validation.saveFailed"));
    } finally {
      setBccSaving(false);
    }
  };

  const r3enabled = r2on;
  const timingRowsRaw = t("recovery.timingRows", { returnObjects: true });
  const timingRows = Array.isArray(timingRowsRaw) ? timingRowsRaw : [];

  const silentActive =
    !!silentUntil && !Number.isNaN(new Date(silentUntil).getTime()) && new Date(silentUntil) > new Date();
  /** Manual pause wins; otherwise reflect listening mode vs normal sending. */
  const showSilentListening = silentActive && !emailPaused;
  const statusAccent = emailPaused ? "#c2410c" : showSilentListening ? "#0369a1" : "#16a34a";
  const statusIcon = emailPaused ? Ic.pause : showSilentListening ? Ic.bellOff : Ic.check;

  return (
    <div>
      {/* ── Stato invio email ── */}
      <div className="ch-card" style={{marginBottom:16}}>
        <h2>{t("recovery.emailStatusTitle")}</h2>
        <p>{showSilentListening ? t("recovery.emailStatusSubtitleSilent") : t("recovery.emailStatusSubtitle")}</p>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 0'}}>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <span className="ch-recovery-email-ic" style={{color: statusAccent}}>{statusIcon}</span>
            <div>
              <div style={{fontWeight:600,fontSize:15,color: statusAccent}}>
                {emailPaused
                  ? t("recovery.emailPausedTitle")
                  : showSilentListening
                    ? t("recovery.emailSilentTitle")
                    : t("recovery.emailActiveTitle")}
              </div>
              <div style={{fontSize:13,color:'#6b7280',marginTop:2}}>
                {emailPaused
                  ? t("recovery.emailPausedHint")
                  : showSilentListening
                    ? t("recovery.emailSilentHint")
                    : t("recovery.emailActiveHint")}
              </div>
            </div>
          </div>
          <button type="button" onClick={onTogglePause} style={{
            padding:'8px 18px',borderRadius:6,fontSize:13,fontWeight:500,cursor:'pointer',whiteSpace:'nowrap',
            display:'inline-flex',alignItems:'center',gap:6,
            background: emailPaused ? '#16a34a' : 'transparent',
            color: emailPaused ? '#fff' : '#ea580c',
            border: emailPaused ? '1px solid #16a34a' : '1px solid #ea580c',
          }}>
            {emailPaused ? <><span style={{display:'inline-flex'}}>{Ic.play}</span>{t("recovery.resume")}</> : <><span style={{display:'inline-flex'}}>{Ic.pause}</span>{t("recovery.pause")}</>}
          </button>
        </div>
      </div>

      {/* ── Copia nascosta (BCC) — i18n: recovery.bcc.* ── */}
      <div className="ch-card" style={{ marginBottom: 16 }}>
        <h2>{t("recovery.bcc.title")}</h2>
        <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, marginBottom: 12 }}>
          {t("recovery.bcc.intro")}
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
          <input
            type="checkbox"
            checked={bccEnabled}
            onChange={(e) => {
              const v = e.target.checked;
              setBccEnabled(v);
              if (v && !bccR && !bccPa && !bccPoe) setBccR(true);
            }}
          />
          <span style={{ fontWeight: 500 }}>{t("recovery.bcc.toggle")}</span>
        </label>
        {!bccEnabled ? (
          <p className="ch-hint" style={{ fontSize: 13, color: '#6b7280', marginBottom: 12, maxWidth: 520, lineHeight: 1.5 }}>
            {t("recovery.bcc.disabledHint")}
          </p>
        ) : null}
        {bccEnabled ? (
          <>
            <div className="ch-field" style={{ marginBottom: 12 }}>
              <label>{t("recovery.bcc.emailLabel")}</label>
              <input
                className="ch-input"
                type="email"
                value={bccEmail}
                onChange={(e) => setBccEmail(e.target.value)}
                style={{ maxWidth: 360 }}
                autoComplete="off"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={bccR} onChange={(e) => setBccR(e.target.checked)} />
                <span>{t("recovery.bcc.typeR")}</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={bccPa} onChange={(e) => setBccPa(e.target.checked)} />
                <span>{t("recovery.bcc.typePa")}</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={bccPoe} onChange={(e) => setBccPoe(e.target.checked)} />
                <span>{t("recovery.bcc.typePoe")}</span>
              </label>
            </div>
          </>
        ) : null}
        {bccErr ? <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 8 }}>{bccErr}</div> : null}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <button type="button" className="ch-btn ch-btn-primary" onClick={saveMerchantBcc} disabled={bccSaving}>
            {bccSaving ? t("recovery.bcc.saving") : t("recovery.bcc.saveBtn")}
          </button>
          {bccSaved ? <span style={{ fontSize: 13, color: '#16a34a' }}>{t("recovery.bcc.saved")}</span> : null}
        </div>
      </div>

      {/* ── Regole sconto ── */}
      <div className="ch-card" style={{marginBottom:16}}>
        <h2>{t("recovery.rulesTitle")}</h2>
        <p>{t("recovery.rulesIntro")}</p>
        <div className="ch-info">{t("recovery.rulesInfo")}</div>
        {rulesLoading ? (
          <div style={{fontSize:14,color:"#9ca3af",padding:"16px 0"}}>{t("recovery.rulesLoading")}</div>
        ) : (
          <>
            <div style={{display:"grid",gridTemplateColumns:"1fr 120px 90px 70px",gap:"8px 16px",padding:"0 0 8px",borderBottom:"1px solid #e5e5e5",marginBottom:4}}>
              {[t("recovery.rulesColBand"), t("recovery.rulesColMax"), t("recovery.rulesColActive"), ""].map((h,i)=><div key={i} style={{fontSize:13,fontWeight:600,color:"#9ca3af",textTransform:"uppercase",letterSpacing:".05em"}}>{h}</div>)}
            </div>
            {rules.map((r,i)=>(
              <div key={r.id} style={{display:"grid",gridTemplateColumns:"1fr 120px 90px 70px",gap:"8px 16px",alignItems:"center",padding:"12px 0",borderBottom:"1px solid #f5f5f5"}}>
                <div style={{fontSize:15,fontWeight:500}}>{localisedDefaultBandLabel(r, t) ?? (r.label || (r.min_cart_value !== undefined ? `€${r.min_cart_value}${r.max_cart_value ? "–€"+r.max_cart_value : "+"}` : "—"))}</div>
                <div>
                  <input type="number" min="0" max="20" value={r.max_discount_percent}
                    onChange={e=>setRules(rules.map((x,j)=>j===i?{...x,max_discount_percent:Math.min(20,Math.max(0,+e.target.value||0))}:x))}
                    className="ch-input ch-input-sm"/>
                  <span style={{fontSize:12,color:"#9ca3af",marginLeft:4}}>{t("recovery.rulesMaxHint")}</span>
                </div>
                <div>
                  <label className="ch-toggle">
                    <input type="checkbox" checked={r.enabled} onChange={e=>setRules(rules.map((x,j)=>j===i?{...x,enabled:e.target.checked}:x))}/>
                    <span className="ch-toggle-track"/>
                  </label>
                </div>
                <div><span className={`ch-status${r.enabled?" success":" neutral"}`}>{r.enabled?"ON":"OFF"}</span></div>
              </div>
            ))}
            <div className="ch-actions">
              <button className="ch-btn ch-btn-primary" onClick={saveRules}>{rulesSaved?<>{Ic.check} {t("recovery.rulesSaved")}</>:t("recovery.rulesSave")}</button>
            </div>
          </>
        )}
      </div>

      {/* ── Protezione margine (recovery) ── */}
      <RecoveryMarginProtectionCard
        blockLinePromo={blockLinePromo}
        setBlockLinePromo={setBlockLinePromo}
        linePromoThreshStr={linePromoThreshStr}
        setLinePromoThreshStr={setLinePromoThreshStr}
        blockCartCouponSnap={blockCartCouponSnap}
        setBlockCartCouponSnap={setBlockCartCouponSnap}
        couponNoStack={couponNoStack}
        setCouponNoStack={setCouponNoStack}
        marginErr={marginErr}
        marginSaving={marginSaving}
        marginSaved={marginSaved}
        onSave={saveMarginProtection}
      />

      {/* ── Soglia e tetti carrelli ad alto valore (recovery caps R2/R3) ── */}
      <div className="ch-card" style={{ marginBottom: 0 }}>
        <h2 style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <HighValueLimitsIcon size={24} />
          <span>{t("recovery.capsTitle")}</span>
        </h2>
        <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.55, marginBottom: 14 }}>
          {t("recovery.capsIntro")}
        </p>

        <div style={{ marginBottom: 16 }}>
          <RecoveryHighValueHelpLegend />
        </div>

        {!seqLoading && (
          <>
            <h3 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 10px", color: "#1a1a1a" }}>{t("recovery.capsYourValues")}</h3>
            <p style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.5, margin: "0 0 14px" }}>
              {t("recovery.capsYourValuesHint")}
            </p>
            <div style={{ display: "grid", gap: 16, maxWidth: 480 }}>
              <div className="ch-field" style={{ margin: 0 }}>
                <label>{t("recovery.capThreshLabel")}</label>
                <div className="ch-hint" style={{ marginBottom: 6 }}>
                  {t("recovery.capThreshHint")}
                </div>
                <input
                  type="number"
                  min={100}
                  className="ch-input ch-input-sm"
                  style={{ maxWidth: 250 }}
                  placeholder={t("recovery.placeholderDefault")}
                  value={capThreshStr}
                  onChange={(e) => setCapThreshStr(e.target.value)}
                />
              </div>
              <div className="ch-field" style={{ margin: 0 }}>
                <label>{t("recovery.capPctLabel")}</label>
                <div className="ch-hint" style={{ marginBottom: 6 }}>
                  {t("recovery.capPctHint")}
                </div>
                <input
                  type="number"
                  min={0}
                  max={20}
                  className="ch-input ch-input-sm"
                  style={{ maxWidth: 250 }}
                  placeholder={t("recovery.placeholderDefault")}
                  value={capMaxPctStr}
                  onChange={(e) => setCapMaxPctStr(e.target.value)}
                />
              </div>
              <div className="ch-field" style={{ margin: 0 }}>
                <label>{t("recovery.capEurLabel")}</label>
                <div className="ch-hint" style={{ marginBottom: 6 }}>
                  {t("recovery.capEurHint")}
                </div>
                <input
                  type="number"
                  min={0}
                  className="ch-input ch-input-sm"
                  style={{ maxWidth: 250 }}
                  placeholder={t("recovery.placeholderDefault")}
                  value={capMaxEurStr}
                  onChange={(e) => setCapMaxEurStr(e.target.value)}
                />
              </div>
            </div>
            <div className="ch-info" style={{ marginTop: 14, marginBottom: 4, background: "#fffbeb", borderColor: "#fde68a" }}>
              {t("recovery.capsSummary")}
            </div>
            {capsErr && (
              <div className="ch-info" style={{ background: "#fff5f5", border: "1px solid #ffcdd2", color: "#c62828", marginTop: 12 }}>{capsErr}</div>
            )}
            <div className="ch-actions" style={{ marginTop: 14 }}>
              <button className="ch-btn ch-btn-primary" onClick={saveRecoveryCaps} disabled={capsSaving}>
                {capsSaving ? t("recovery.capsSaving") : capsSaved ? <>{Ic.check} {t("recovery.rulesSaved")}</> : t("recovery.capsSave")}
              </button>
            </div>
          </>
        )}
      </div>

      {/* ── Sequenza Email ── */}
      <div className="ch-card" style={{marginTop:16}}>
        <h2>{tEmailUi("sequenceSectionTitle")}</h2>
        <div className="ch-info" style={{borderColor:"#fbbf24",background:"#fffbeb"}}>
          <span style={{display:'inline-flex',alignItems:'center',gap:6}}><span style={{display:'inline-flex',color:'var(--text2)'}}>{Ic.settings}</span><strong>{t("recovery.aiBanner")}</strong></span>{" "}
          {t("recovery.aiBannerRest")}
        </div>

        {/* ── Timing ── */}
        {!seqLoading && (
          <div style={{marginTop:16,marginBottom:4}}>
            <h3 style={{fontSize:15,fontWeight:600,marginBottom:12}}>{t("recovery.timingTitle")}</h3>

            {/* Automatico */}
            <div
              onClick={()=>setTimingMode('auto')}
              style={{
                border: `1.5px solid ${timingMode === "auto" ? "#a7d4e4" : "#e5e5e5"}`,
                borderRadius: 8,
                padding: "14px 18px",
                marginBottom: 10,
                cursor: "pointer",
                background: timingMode === "auto" ? "rgba(10, 133, 207, 0.08)" : "#fff",
              }}
            >
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:timingMode==='auto'?10:0}}>
                <div style={{width:16,height:16,borderRadius:'50%',border:`2px solid ${timingMode==='auto'?'#0A85CF':'#9ca3af'}`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  {timingMode==='auto' && <div style={{width:8,height:8,borderRadius:'50%',background:'#0A85CF'}}/>}
                </div>
                <div>
                  <span style={{fontWeight:600,fontSize:14}}>{t("recovery.timingAuto")}</span>
                  <span style={{fontSize:13,color:'#9ca3af',marginLeft:8}}>{t("recovery.timingRecommended")}</span>
                </div>
              </div>
              {timingMode === "auto" && (
                <div style={{ paddingLeft: 26 }}>
                  <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 12px", lineHeight: 1.55 }}>
                    {t("recovery.timingAutoDesc")}
                  </p>
                  <div className="ch-timing-matrix-wrap">
                    <div className="ch-timing-matrix" role="region" aria-label={t("recovery.timingMatrixAria")}>
                      <div className="ch-timing-matrix__accent" aria-hidden="true" />
                      <table>
                        <thead>
                          <tr>
                            {[t("recovery.thCartValue"), "R1", "R2", "R3"].map((h) => (
                              <th key={h} scope="col">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {timingRows.map((row) => (
                            <tr key={row.band}>
                              <td className="ch-timing-matrix__fascia">{row.band}</td>
                              <td><span className="ch-timing-matrix__step">{row.r1}</span></td>
                              <td><span className="ch-timing-matrix__step">{row.r2}</span></td>
                              <td><span className="ch-timing-matrix__step">{row.r3}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="ch-timing-matrix__foot">
                        {t("recovery.timingMatrixFoot")}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Personalizzato */}
            <div
              onClick={()=>setTimingMode('custom')}
              style={{
                border: `1.5px solid ${timingMode === "custom" ? "#a7d4e4" : "#e5e5e5"}`,
                borderRadius: 8,
                padding: "14px 18px",
                cursor: "pointer",
                background: timingMode === "custom" ? "rgba(10, 133, 207, 0.08)" : "#fff",
              }}
            >
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:timingMode==='custom'?12:0}}>
                <div style={{width:16,height:16,borderRadius:'50%',border:`2px solid ${timingMode==='custom'?'#0A85CF':'#9ca3af'}`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  {timingMode==='custom' && <div style={{width:8,height:8,borderRadius:'50%',background:'#0A85CF'}}/>}
                </div>
                <span style={{fontWeight:600,fontSize:14}}>{t("recovery.timingCustom")}</span>
              </div>
              {timingMode==='custom' && (
                <div style={{paddingLeft:26}} onClick={e=>e.stopPropagation()}>
                  <p style={{fontSize:13,color:'#6b7280',margin:'0 0 12px'}}>{t("recovery.timingCustomDesc")}</p>
                  {[
                    {label:t("recovery.r1Row"),val:r1delay,set:v=>setR1delay(Math.max(20,Math.min(360,v))),unit:t("recovery.unitMin"),hint:t("recovery.hintR1")},
                    {label:t("recovery.r2Row"),val:r2delay,set:v=>setR2delay(Math.max(1,Math.min(72,v))),unit:t("recovery.unitHours"),hint:t("recovery.hintR2")},
                    {label:t("recovery.r3Row"),val:r3delay,set:v=>setR3delay(Math.max(1,Math.min(168,v))),unit:t("recovery.unitHours"),hint:t("recovery.hintR3")},
                  ].map(row=>(
                    <div key={row.label} style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
                      <span style={{fontSize:14,color:'#1a1a1a',minWidth:180}}>{row.label}</span>
                      <input type="number" className="ch-input ch-input-sm" style={{width:70}} value={row.val} onChange={e=>row.set(+e.target.value||0)}/>
                      <span style={{fontSize:13,color:'#6b7280'}}>{row.unit}</span>
                      <span style={{fontSize:12,color:'#9ca3af'}}>· {row.hint}</span>
                    </div>
                  ))}
                  {(r1delay/60 + r2delay + r3delay) < 24 && (
                    <div style={{fontSize:13,color:'#0A85CF',background:'#f0faf8',border:'1px solid #b2d8d1',borderRadius:6,padding:'8px 12px',marginTop:4}}>
                      <span style={{display:'inline-flex',alignItems:'center',gap:6}}><span style={{display:'inline-flex',color:'#b45309',flexShrink:0}}>{Ic.alert}</span>{t("recovery.timingAggressiveWarn")}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {seqLoading ? (
          <div style={{fontSize:14,color:"#9ca3af",padding:"16px 0"}}>{t("recovery.rulesLoading")}</div>
        ) : (
          <>
            {/* R1 */}
            <div style={{padding:"16px 0",borderBottom:"1px solid #e5e5e5"}}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:6}}>
                <div style={{width:36,height:20,background:"#d1fae5",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {Ic.check}
                </div>
                <div>
                  <div style={{fontWeight:600,fontSize:15}}>{t("recovery.r1CardTitle")}</div>
                  <div style={{fontSize:13,color:"#6b7280",marginTop:2}}>
                    {timingMode==='auto' ? t("recovery.r1CardSubAuto") : t("recovery.r1CardSubCustom", { minutes: r1delay })}
                  </div>
                </div>
                <span className="ch-status success" style={{marginLeft:"auto"}}>{t("recovery.r1AlwaysOn")}</span>
              </div>
              <div style={{paddingLeft:44,marginTop:4}}>
                <div className="ch-hint">{t("recovery.r1Hint")}</div>
              </div>
            </div>

            {/* R2 */}
            <div style={{padding:"16px 0",borderBottom:"1px solid #e5e5e5"}}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:6}}>
                <label className="ch-toggle"><input type="checkbox" checked={r2on} onChange={e=>{setR2on(e.target.checked); if(!e.target.checked) setR3on(false);}}/><span className="ch-toggle-track"/></label>
                <div>
                  <div style={{fontWeight:600,fontSize:15}}>{t("recovery.r2CardTitle")}</div>
                  <div style={{fontSize:13,color:"#6b7280",marginTop:2}}>{timingMode==='auto'?t("recovery.r2CardSubAuto"):t("recovery.r2CardSubCustom", { hours: r2delay })}</div>
                </div>
                <span className={`ch-status${r2on?" success":" neutral"}`} style={{marginLeft:"auto"}}>{r2on?"ON":"OFF"}</span>
              </div>
              {r2on && (
                <div className="ch-hint" style={{paddingLeft:44,marginTop:6}}>
                  {t("recovery.r2Hint")}
                  {timingMode==='auto' && <span>{t("recovery.r2HintTimingAuto")}</span>}
                  {timingMode==='custom' && <span>{t("recovery.r2HintTimingCustom", { hours: r2delay })}</span>}
                </div>
              )}
            </div>

            {/* R3 */}
            <div style={{padding:"16px 0",opacity:r3enabled?1:.5}}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:6}}>
                <label className="ch-toggle"><input type="checkbox" checked={r3on&&r3enabled} disabled={!r3enabled} onChange={e=>setR3on(e.target.checked)}/><span className="ch-toggle-track"/></label>
                <div>
                  <div style={{fontWeight:600,fontSize:15}}>{t("recovery.r3CardTitle")}</div>
                  <div style={{fontSize:13,color:"#6b7280",marginTop:2}}>{timingMode==='auto'?t("recovery.r3CardSubAuto"):t("recovery.r3CardSubCustom", { hours: r3delay })}</div>
                  {!r3enabled && <div style={{fontSize:13,color:"#0A85CF",marginTop:2,display:'flex',alignItems:'center',gap:6}}><span style={{display:'inline-flex',flexShrink:0}}>{Ic.alert}</span>{t("recovery.r3RequiresR2")}</div>}
                </div>
                <span className={`ch-status${r3on&&r3enabled?" success":" neutral"}`} style={{marginLeft:"auto"}}>{r3on&&r3enabled?"ON":"OFF"}</span>
              </div>
              {r3enabled && r3on && (
                <div style={{paddingLeft:44,marginTop:6,fontSize:13,color:"#6b7280",background:"#f9f9f9",border:"1px solid #e5e5e5",borderRadius:6,padding:"8px 12px"}}>
                  <span style={{display:'inline-flex',alignItems:'flex-start',gap:6}}><span style={{display:'inline-flex',flexShrink:0,marginTop:1,color:'var(--text2)'}}>{Ic.lightbulb}</span><span>{t("recovery.r3HintCoupon")}</span></span>
                  {timingMode==='auto' ? t("recovery.r3HintTimingAuto") : t("recovery.r3HintTimingCustom", { hours: r3delay })}
                </div>
              )}
            </div>

            <div className="ch-actions">
              <button className="ch-btn ch-btn-primary" onClick={saveSequence}>{seqSaved?<>{Ic.check} {t("recovery.rulesSaved")}</>:t("recovery.saveSequence")}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Settings({ shop, emailPaused, onTogglePause, onShopPatch, settingsTabHint, onSettingsTabHintConsumed, onOpenPriceAlertStats }) {
  const { t } = useTranslation("settings");
  const [tab,setTab]=useState("recovery"); const [copied,setCopied]=useState(false); const [saved,setSaved]=useState(false);
  const [rotateLoading,setRotateLoading]=useState(false);
  const [rotateErr,setRotateErr]=useState("");
  const [revealedKey,setRevealedKey]=useState(null);
  const [shopName, setShopName] = useState(shop?.name||'');

  useEffect(() => {
    if (!settingsTabHint) return;
    if (settingsTabHint === "price-alert") setTab("price-alert");
    if (settingsTabHint === "onsite-popup") setTab("onsite-popup");
    if (settingsTabHint === "price-alert" || settingsTabHint === "onsite-popup") onSettingsTabHintConsumed?.();
  }, [settingsTabHint, onSettingsTabHintConsumed]);

  // Sincronizza api_key dal server quando apri Account & API (evita Copia con chiave vecchia dopo rotazione / altra scheda).
  useEffect(() => {
    if (tab !== 'account' || !shop?.id || typeof onShopPatch !== 'function') return;
    let cancelled = false;
    (async () => {
      try {
        const d = await api(`/shops/${shop.id}`, { method: 'GET' });
        if (cancelled || !d || d.error || !d.api_key) return;
        onShopPatch({ api_key: d.api_key });
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [tab, shop?.id, onShopPatch]);

  const rotateApiKey = async () => {
    setRotateErr("");
    if (!window.confirm(t("confirm.rotateApiKey"))) return;
    setRotateLoading(true);
    try {
      const d = await api(`/shops/${shop?.id}/rotate-api-key`, { method: "POST" });
      if (d.error) {
        setRotateErr(typeof d.error === "string" ? d.error : t("account.rotateErrGeneric"));
        return;
      }
      if (d.api_key && typeof onShopPatch === "function") {
        onShopPatch({ api_key: d.api_key });
        setRevealedKey(d.api_key);
      }
    } catch {
      setRotateErr(t("account.networkErr"));
    } finally {
      setRotateLoading(false);
    }
  };
  const save=async()=>{
    if (tab==='account') {
      try {
        const updated = await api(`/shops/${shop?.id}`, { method:'PUT', body: JSON.stringify({ name: shopName }) });
        const current = JSON.parse(localStorage.getItem('carthub_shop')||'{}');
        localStorage.setItem('carthub_shop', JSON.stringify({
          ...current,
          name: updated.name || shopName,
          ...(updated.language != null ? { language: updated.language } : {})
        }));
      } catch(e) { console.error('Save shop error:', e); }
    }
    setSaved(true); setTimeout(()=>setSaved(false),2000);
  };
  const copy = () => {
    const k = (revealedKey || shop?.api_key || '').trim();
    if (!k) return;
    navigator.clipboard.writeText(k);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div>
      <div className="ch-tabs">
        {[
          ["recovery", "tabs.recovery"],
          ["email", "tabs.email"],
          ["invio", "tabs.dns"],
          ["price-alert", "tabs.price_alert_customize"],
          ["onsite-popup", "tabs.onsite_popup"],
          ["account", "tabs.account"],
        ].map(([k, tk]) => (
          <div key={k} className={`ch-tab${tab===k?" active":""}`} onClick={()=>setTab(k)}>{t(tk)}</div>
        ))}
      </div>
      {tab==="recovery"&&(
        <RecoverySettings
          shopId={shop?.id}
          emailPaused={emailPaused}
          silentUntil={shop?.silent_until ?? null}
          onTogglePause={onTogglePause}
        />
      )}
      {tab==="email"&&(
        <div className="ch-card">
          <EmailBranding shopId={shop?.id}/>
        </div>
      )}
      {tab==="invio"&&(
        <div className="ch-card">
          <EmailTier shopId={shop?.id} shopDomain={shop?.domain}/>
        </div>
      )}
      {tab==="price-alert"&& shop && (
        <>
          <PriceAlertWpScopeReadonly shop={shop} />
          <PriceAlertSetup shop={shop} onOpenStats={onOpenPriceAlertStats} />
        </>
      )}
      {tab==="onsite-popup" && shop?.id != null && (
        <OnSitePopupSettings shopId={shop.id} />
      )}
      {tab==="account"&&(
        <div className="grid-2">
          <div className="ch-card">
            <h2>{t("account.apiTitle")}</h2>
            <p>{t("account.apiIntro")}</p>
            <div className="ch-field">
              <label>{t("account.apiKeyLabel")}</label>
              <div className="copy-field">
                <div className="copy-value">{shop?.api_key?shop.api_key.slice(0,24)+"••••":t("account.apiKeyUnavailable")}</div>
                <button type="button" className="ch-btn ch-btn-sm" onClick={copy} disabled={!(revealedKey || shop?.api_key)}>{copied?<>{Ic.check} {t("account.copied")}</>:<>{Ic.copy} {t("account.copy")}</>}</button>
              </div>
              <div className="ch-hint">{t("account.apiKeyHint")}</div>
              {rotateErr ? <div className="ch-hint" style={{color:"#b91c1c",marginTop:8}}>{rotateErr}</div> : null}
              {revealedKey ? (
                <div style={{marginTop:12,padding:12,borderRadius:8,background:"#fffbeb",border:"1px solid #fcd34d",fontSize:13}}>
                  <strong style={{display:"block",marginBottom:8,color:"#92400e"}}>{t("account.newKeyTitle")}</strong>
                  <code style={{display:"block",wordBreak:"break-all",fontSize:12,marginBottom:8}}>{revealedKey}</code>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    <button type="button" className="ch-btn ch-btn-sm" onClick={()=>{ navigator.clipboard.writeText(revealedKey); setCopied(true); setTimeout(()=>setCopied(false),2000); }}>{Ic.copy} {t("account.copyFullKey")}</button>
                    <button type="button" className="ch-btn ch-btn-sm" style={{background:"transparent",border:"1px solid #d1d5db"}} onClick={()=>setRevealedKey(null)}>{t("account.hide")}</button>
                  </div>
                </div>
              ) : null}
              <div className="ch-actions" style={{marginTop:12,paddingTop:0,borderTop:"none"}}>
                <button type="button" className="ch-btn ch-btn-sm" style={{background:"#fff",border:"1px solid #d1d5db",color:"#374151"}} onClick={rotateApiKey} disabled={rotateLoading || !shop?.id}>
                  {rotateLoading ? t("account.rotateWait") : t("account.rotateBtn")}
                </button>
              </div>
            </div>
          </div>
          <div className="ch-card">
            <h2>{t("account.shopInfoTitle")}</h2>
            <p>{t("account.shopInfoIntro")}</p>
            <div className="ch-field"><label>{t("account.shopNameLabel")}</label><input className="ch-input full" value={shopName} onChange={e=>setShopName(e.target.value)}/></div>
            <div className="ch-field"><label>{t("account.domainLabel")}</label><input className="ch-input full" defaultValue={shop?.domain||"myshop.com"} readOnly style={{color:"#9ca3af",cursor:"default"}}/><div className="ch-hint">{t("account.domainHint")}</div></div>
            <div className="ch-actions"><button className="ch-btn ch-btn-primary" onClick={save}>{saved?<>{Ic.check} {t("account.saved")}</>:t("account.save")}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
